import { Controller } from "@hotwired/stimulus"

/**
 * Bulk Select Controller
 *
 * Owns "what is ticked" for a data table: the header box, the row boxes, the action bar and the
 * hidden field the bar submits.
 *
 * A page can opt into selecting rows the browser never rendered by supplying a matching count and
 * the banner targets. Ticking every box on a page then offers to extend the selection to the whole
 * filtered set, and from that point the selection is described to the server as "everything
 * matching these filters, except these ids" so it keeps meaning the same thing across pages.
 */
export default class extends Controller {
  static targets = [
    "checkbox", "selectAll", "bar", "count", "form", "idsField", "actionButton",
    "banner", "bannerMessage", "selectAllMatchingButton", "clearButton",
    "allMatchingField", "excludedIdsContainer"
  ]

  static values = {
    matchingCount: { type: Number, default: 0 }
  }

  connect() {
    this.injectSelectAllCheckbox()
    this.resetMatching()
    this.updateBar()
  }

  injectSelectAllCheckbox() {
    const headerCell = this.element.querySelector("th.data-table-checkbox-col")
    if (!headerCell || headerCell.querySelector("input")) return

    const cb = document.createElement("input")
    cb.type = "checkbox"
    cb.className = "bulk-select-checkbox"
    cb.dataset.bulkSelectTarget = "selectAll"
    cb.dataset.action = "change->bulk-select#toggleAll"
    headerCell.textContent = ""
    headerCell.appendChild(cb)
  }

  toggle(event) {
    this.trackExclusion(event?.target)
    this.syncSelectAll()
    this.updateBar()
  }

  toggleAll() {
    const checked = this.selectAllTarget.checked
    if (!checked) this.resetMatching()
    this.selectableCheckboxes().forEach(cb => { cb.checked = checked })
    this.updateBar()
  }

  selectAllMatching() {
    this.allMatching = true
    this.excludedIds = new Set()
    this.selectableCheckboxes().forEach(cb => { cb.checked = true })
    this.syncSelectAll()
    this.updateBar()
  }

  updateBar() {
    const count = this.selectedCount()
    const hasSelection = count > 0

    if (this.hasBarTarget) {
      this.barTarget.hidden = !hasSelection
      if (hasSelection && this.hasCountTarget) {
        this.countTarget.textContent = `${count} selected`
      }
    }

    if (this.hasIdsFieldTarget) {
      this.idsFieldTarget.value = this.allMatching ? "" : this.selectedIds().join(",")
    }

    if (this.hasActionButtonTarget) {
      this.actionButtonTargets.forEach((button) => {
        button.disabled = !hasSelection
      })
    }

    this.syncSelectionFields()
    this.renderBanner()
  }

  selectedIds() {
    return this.selectableCheckboxes()
      .filter(cb => cb.checked)
      .map(cb => cb.value)
  }

  visibleCheckboxes() {
    return this.checkboxTargets.filter(cb => !cb.closest("tr[hidden]"))
  }

  selectableCheckboxes() {
    return this.visibleCheckboxes().filter(cb => !cb.disabled)
  }

  syncSelectAll() {
    const selectable = this.selectableCheckboxes()
    if (!this.hasSelectAllTarget || selectable.length === 0) return
    this.selectAllTarget.checked = selectable.every(cb => cb.checked)
  }

  clearSelection() {
    this.resetMatching()

    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = false
    })

    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    }

    this.updateBar()
  }

  // --- selecting beyond the current page ---------------------------------------

  resetMatching() {
    this.allMatching = false
    this.excludedIds = new Set()
  }

  selectedCount() {
    if (this.allMatching) return Math.max(this.matchingCountValue - this.excludedIds.size, 0)

    return this.selectedIds().length
  }

  trackExclusion(checkbox) {
    if (!this.allMatching || !checkbox?.value) return

    if (checkbox.checked) {
      this.excludedIds.delete(checkbox.value)
    } else {
      this.excludedIds.add(checkbox.value)
    }
  }

  syncSelectionFields() {
    if (this.hasAllMatchingFieldTarget) {
      this.allMatchingFieldTarget.value = this.allMatching ? "true" : ""
    }

    if (!this.hasExcludedIdsContainerTarget) return

    this.excludedIdsContainerTarget.textContent = ""
    if (!this.allMatching) return

    this.excludedIds.forEach((id) => {
      const field = document.createElement("input")
      field.type = "hidden"
      field.name = "selection[excluded_ids][]"
      field.value = id
      this.excludedIdsContainerTarget.appendChild(field)
    })
  }

  renderBanner() {
    if (!this.hasBannerTarget) return

    const selectable = this.selectableCheckboxes()
    const checked = this.selectedIds().length
    const wholePageChecked = selectable.length > 0 && checked >= selectable.length

    if (this.allMatching) {
      this.showBanner(`All ${this.selectedCount()} matching these filters are selected.`, { offerSelectAll: false })
      return
    }

    if (wholePageChecked && this.matchingCountValue > selectable.length) {
      this.showBanner(`All ${checked} on this page are selected.`, { offerSelectAll: true })
      return
    }

    this.bannerTarget.hidden = true
  }

  showBanner(message, { offerSelectAll }) {
    this.bannerTarget.hidden = false
    if (this.hasBannerMessageTarget) this.bannerMessageTarget.textContent = message
    if (this.hasSelectAllMatchingButtonTarget) this.selectAllMatchingButtonTarget.hidden = !offerSelectAll
    if (this.hasClearButtonTarget) this.clearButtonTarget.hidden = offerSelectAll
  }
}
